import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: '🔐',
      title: 'Secure Banking',
      description: 'Bank-grade security with JWT authentication and encrypted transactions'
    },
    {
      icon: '⚡',
      title: 'Instant Transfers',
      description: 'Send money instantly to anyone, anytime, anywhere'
    },
    {
      icon: '📱',
      title: 'Mobile Ready',
      description: 'Bank on the go with our responsive platform'
    },
    {
      icon: '💳',
      title: 'Multiple Accounts',
      description: 'Checking, savings, and business accounts all in one place'
    },
    {
      icon: '📊',
      title: 'Real-time Updates',
      description: 'Track your finances with live balance updates'
    },
    {
      icon: '🎯',
      title: 'Easy Management',
      description: 'Intuitive dashboard for effortless money management'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Small Business Owner',
      content: 'Omega Trust Bank has transformed how I manage my business finances. The interface is intuitive and transfers are instant!',
      rating: 5,
      image: '/images/sarah.jpg'
    },
    {
      name: 'Michael Chen',
      role: 'Freelancer',
      content: 'Finally, a banking platform that understands modern needs. Love the mobile experience!',
      rating: 5,
      image: '/images/michael.jpg'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Tech Entrepreneur',
      content: 'Security and speed combined. Omega Trust Bank is exactly what I needed for my growing startup.',
      rating: 5,
      image: '/images/emily.jpg'
    }
  ];

  const stats = [
    { number: '50K+', label: 'Active Users' },
    { number: '$2B+', label: 'Transferred' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Support' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 via-white to-primary-50 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none"
          style={{ backgroundImage: 'url(/images/hero-bg.jpg)' }}
        ></div>
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-block bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-semibold">
                Secure Banking
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-neutral-900 leading-tight">
                Banking Made
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600"> Simple</span>
              </h1>
              <p className="text-xl text-neutral-600">
                Experience the future of digital banking. Secure, fast, and built for everyone.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/register')}
                  className="bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition transform hover:scale-105 shadow-lg">
                  Create Free Account
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-neutral-50 transition border-2 border-primary-600">
                  Sign In
                </button>
              </div>
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex -space-x-2">
                  {[
                    "/images/user2.jpg",
                    "/images/user3.jpg",
                    "/images/user1.jpg",
                    "/images/user4.jpg"
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={`${src}?${i}`}
                      alt="Omega Trust Bank user"
                      className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-sm"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.outerHTML = `<div class="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 border-2 border-white"></div>`;
                      }}
                    />
                  ))}
                </div>
                <div className="text-sm text-neutral-600">
                  <span className="font-bold text-neutral-900">50,000+</span> users trust Omega Trust Bank
                </div>
              </div>
            </div>

            {/* Hero Illustration */}
<div className="relative" style={{ transform: `translateY(${scrollY * 0.1}px)` }}>
  <div className="relative py-8 px-4">
    {/* Main Bank Card - solid blue gradient, bold, white text, strong shadow */}
    <div className="relative z-10 mx-auto max-w-sm bg-gradient-to-br from-blue-500 via-blue-600 to-blue-800 p-8 rounded-3xl shadow-2xl border border-blue-400/40 transform hover:scale-[1.02] transition-all duration-300">

      {/* Card header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <p className="text-white text-xs tracking-widest font-bold mb-2 uppercase">
            Checking Account
          </p>
          <p className="text-4xl font-extrabold text-white tracking-tight">
            $15,750.00
          </p>
        </div>
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
            <path
              fillRule="evenodd"
              d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Card details */}
      <div className="space-y-4 border-t border-white/30 pt-5">
        <div className="flex justify-between items-center">
          <span className="text-white text-xs font-bold uppercase tracking-wider">
            Account Number
          </span>
          <span className="font-mono font-extrabold text-white text-xl tracking-[0.25em]">
            •••• 4829
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-white text-xs font-bold uppercase tracking-wider">
            Valid Thru
          </span>
          <span className="font-mono font-extrabold text-white text-xl tracking-widest">
            12/27
          </span>
        </div>
      </div>

      {/* Card footer */}
      <div className="flex items-center justify-between mt-8 pt-5 border-t border-white/30">
        <span className="text-white text-sm font-extrabold tracking-widest">
          OMEGA TRUST BANK
        </span>
        <div className="flex -space-x-3">
          <div className="w-8 h-8 rounded-full bg-white/40"></div>
          <div className="w-8 h-8 rounded-full bg-white/70"></div>
        </div>
      </div>
    </div>
  </div>
</div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary-700 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-primary-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-neutral-600">
              Powerful features to manage your money with ease
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-neutral-100"
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-neutral-900 mb-3">{feature.title}</h3>
                <p className="text-neutral-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 bg-neutral-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-neutral-600">
              Three simple steps to better banking
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Create Account', desc: 'Sign up with your email in less than 2 minutes' },
              { step: '2', title: 'Add Accounts', desc: 'Create checking, savings, or business accounts' },
              { step: '3', title: 'Start Banking', desc: 'Transfer money, track transactions, and more' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-600 to-accent-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-3">{item.title}</h3>
                  <p className="text-neutral-600">{item.desc}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 relative bg-neutral-50">
  <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5 pointer-events-none z-0"
    style={{ backgroundImage: 'url(/images/testimonial-bg.jpg)' }}
  ></div>
  <div className="relative z-10 container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Trusted by Thousands
            </h2>
            <p className="text-xl text-neutral-600">
              See what our users have to say
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-2xl">
              <div className="flex mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 text-secondary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-xl text-neutral-700 mb-6 italic">
                "{testimonials[currentTestimonial].content}"
              </p>
              <div className="flex items-center">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <p className="font-bold text-neutral-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-neutral-600">{testimonials[currentTestimonial].role}</p>
                </div>
              </div>
            </div>

            {/* Testimonial Dots */}
            <div className="flex justify-center mt-8 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition ${
                    index === currentTestimonial ? 'bg-primary-600 w-8' : 'bg-neutral-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-primary-100">
            Join thousands of users managing their finances smarter
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-neutral-100 transition transform hover:scale-105 shadow-lg text-lg"
          >
            Create Your Free Account
          </button>
          <p className="mt-4 text-primary-100 text-sm">
            No credit card required • Takes less than 2 minutes
          </p>
        </div>
      </section>

      {/* Footer */}
    {/* Footer */}
<footer className="bg-neutral-900 text-neutral-300 py-12 px-4">
  <div className="container mx-auto">
    <div className="grid md:grid-cols-4 gap-8 mb-8">
      <div>
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center">
            <img
              src="/images/logo.png"
              alt="Omega Trust Bank Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <span className="text-xl font-bold text-white">Omega Trust Bank</span>
        </div>
        <p className="text-sm mb-4">
          Modern banking made simple, secure, and accessible for everyone.
        </p>

        {/* Global Presence Flags - SVG Implementation */}
        <div className="mb-4">
          <p className="text-xs text-neutral-400 mb-3 font-semibold uppercase tracking-wide">
            Serving Customers Worldwide
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              {
                code: 'us',
                name: 'United States',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden relative bg-blue-600">
                    {/* US Flag Stripes */}
                    <div className="absolute top-0 left-0 w-2/5 h-1/2 bg-red-600"></div>
                    <div className="absolute top-0 left-2/5 w-3/5 h-1/2 bg-white"></div>
                    <div className="absolute top-1/2 left-0 w-full h-1/6 bg-red-600"></div>
                    <div className="absolute top-2/3 left-0 w-full h-1/6 bg-white"></div>
                    <div className="absolute top-5/6 left-0 w-full h-1/6 bg-red-600"></div>
                  </div>
                )
              },
              {
                code: 'gb',
                name: 'United Kingdom',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden relative bg-blue-800">
                    {/* UK Flag Crosses */}
                    <div className="absolute inset-0 bg-white" style={{
                      clipPath: 'polygon(0 0, 100% 0, 100% 40%, 40% 40%, 40% 100%, 0 100%)'
                    }}></div>
                    <div className="absolute inset-0 bg-red-600" style={{
                      clipPath: 'polygon(0 30%, 30% 30%, 30% 0, 70% 0, 70% 30%, 100% 30%, 100% 70%, 70% 70%, 70% 100%, 30% 100%, 30% 70%, 0 70%)'
                    }}></div>
                  </div>
                )
              },
              {
                code: 'eu',
                name: 'European Union',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden bg-blue-600 flex items-center justify-center">
                    <div className="text-yellow-300 text-xs font-bold">EU</div>
                  </div>
                )
              },
              {
                code: 'ca',
                name: 'Canada',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden bg-red-600 flex items-center justify-center">
                    <div className="text-white text-xs font-bold">CA</div>
                  </div>
                )
              },
              {
                code: 'au',
                name: 'Australia',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden relative bg-blue-800">
                    {/* Australian Flag */}
                    <div className="absolute top-0 left-0 w-1/4 h-1/2 bg-red-600"></div>
                    <div className="absolute top-0 left-1/4 w-3/4 h-1/2 bg-white"></div>
                    <div className="absolute top-1/2 left-0 w-full h-1/2 bg-blue-800"></div>
                    <div className="absolute top-1/4 left-1/6 w-1 h-1 bg-white rounded-full"></div>
                  </div>
                )
              },
              {
                code: 'jp',
                name: 'Japan',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden bg-white flex items-center justify-center">
                    <div className="w-4 h-4 bg-red-600 rounded-full"></div>
                  </div>
                )
              },
              {
                code: 'sg',
                name: 'Singapore',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden relative bg-red-600">
                    {/* Singapore Flag */}
                    <div className="absolute top-0 left-0 w-2/5 h-full bg-white"></div>
                    <div className="absolute top-0 left-0 w-1/3 h-1/3 flex items-center justify-center">
                      <div className="text-red-600 text-xs">★</div>
                    </div>
                  </div>
                )
              },
              {
                code: 'ae',
                name: 'UAE',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden flex">
                    {/* UAE Flag */}
                    <div className="w-1/4 bg-red-600"></div>
                    <div className="w-3/4 flex flex-col">
                      <div className="h-1/3 bg-green-600"></div>
                      <div className="h-1/3 bg-white"></div>
                      <div className="h-1/3 bg-black"></div>
                    </div>
                  </div>
                )
              },
              {
                code: 'cn',
                name: 'China',
                flag: (
                  <div className="w-8 h-6 rounded border border-neutral-600 overflow-hidden bg-red-600 flex items-center justify-center">
                    <div className="text-yellow-400 text-lg">★</div>
                  </div>
                )
              }
            ].map((country) => (
              <div
                key={country.code}
                className="relative group"
                title={`${country.name} Customers`}
              >
                <div className="cursor-default transition-transform hover:scale-110 hover:shadow-lg">
                  {country.flag}
                </div>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-neutral-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-10 shadow-xl border border-neutral-700">
                  <div className="font-semibold text-white">{country.name}</div>
                  <div className="text-neutral-300 mt-1">Banking Services</div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-neutral-800"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Regulatory Badges */}
        <div className="flex items-center space-x-3">
          <div className="bg-green-800 text-green-100 px-2 py-1 rounded text-xs font-semibold">
            FDIC Insured
          </div>
          <div className="bg-blue-800 text-blue-100 px-2 py-1 rounded text-xs font-semibold">
            SSL Secured
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-bold text-white mb-4">Product</h4>
        <ul className="space-y-2 text-sm">
          <li><a href="#features" className="hover:text-white transition">Features</a></li>
          <li><a href="/accounts" className="hover:text-white transition">Accounts</a></li>
          <li><a href="/transfer" className="hover:text-white transition">Transfers</a></li>
          <li><a href="/transactions" className="hover:text-white transition">Transactions</a></li>
        </ul>
      </div>

      <div>
        <h4 className="font-bold text-white mb-4">Company</h4>
        <ul className="space-y-2 text-sm">
          <li><a href="/about" className="hover:text-white transition">About Us</a></li>
          <li><a href="#testimonials" className="hover:text-white transition">Testimonials</a></li>
          <li><a href="#how-it-works" className="hover:text-white transition">How It Works</a></li>
        </ul>
      </div>

      <div>
        <h4 className="font-bold text-white mb-4">Legal</h4>
        <ul className="space-y-2 text-sm">
          <li><a href="/about#privacy" className="hover:text-white transition">Privacy Policy</a></li>
          <li><a href="/about#privacy" className="hover:text-white transition">Security</a></li>
        </ul>
      </div>
    </div>

    {/* Global Services Section */}
    <div className="border-t border-neutral-800 pt-8">
      <div className="grid md:grid-cols-2 gap-8 mb-6">
        <div>
          <h5 className="font-semibold text-white mb-3">International Services</h5>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-green-400">✓</span>
              <span>Multi-currency Accounts</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-400">✓</span>
              <span>Global Transfers</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-400">✓</span>
              <span>Forex Services</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-400">✓</span>
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
        <div>
          <h5 className="font-semibold text-white mb-3">Contact Global Support</h5>
          <div className="text-sm space-y-1">
            <div className="flex items-center space-x-2">
              <span>🌍</span>
              <span>24/7 International Helpline</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>📧</span>
              <span>omegatrbnk01@aol.com</span>
            </div>
            <div className="flex items-center space-x-2">
              <span>🕒</span>
              <span>Always Available, Every Timezone</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="border-t border-neutral-800 pt-8 text-center text-sm">
      <p>© 2025 Omega Trust Bank. Serving customers worldwide with secure, modern banking solutions.</p>
    </div>
  </div>
</footer>
    </div>
  );
};

export default Home;
